"use client";
import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Icon,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Skeleton,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  tooltipClasses,
  alpha,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CASH from "../../../assets/cash.jpg";
import UPI from "../../../assets/upi.png";
import MessageIcon from "@mui/icons-material/Message";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Coupon from "./CouponDialog";
import Image from "next/image";
import {
  Address,
  Customer,
  OrderServiceRequest,
  OrderSummary,
} from "@/app/classes/main";
import { ApiService } from "../../../services/AppService";
import {
  OrderRequest,
  ResponseObject,
  CalendarResponse,
  BuildTempSlot,
  ServiceHub,
} from "../../../classes/main";
import { getToken } from "../../../services/HolderService";
import { AddToCartService, Cart, MainCart } from "../../../services/cart";
import { PaymentModeResponse } from "../../../classes/main";
import CloseIcon from "@mui/icons-material/Close";
import { useRouter } from "next/navigation";
import { useCurrencyCode } from "../../genericComponents/helper-functions/CurrencySymbol";
import { PlansPayments } from "../../plans/PlansPayment";
import ToolTipSafetyInfo from "./ToolTipSafetyInfo";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { trackEvent } from "@/app/utils/branch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import toast from "react-hot-toast";
import { CircularLoader } from "../../genericComponents/loader/CircularLoader";
import {
  PAYMENT_MINT,
  PAYMENT_MINT_DARK,
  paymentDialogPaperSx,
  paymentGradient,
  paymentMethodRowSx,
  paymentPrimaryButtonSx,
} from "./paymentUi";

interface PaymentProps {
  openPaymentdialog: any;
  handleClosePaymentDialog: any;
  setLoading: any;
  cid: any;
  stopSetLoading: any;
  disposableKitCount: any;
  circularLoader: any;
  setCircularLoader: any;
}

interface Hub {
  id: number;
}

interface WalletAmount {
  walletAmount: number;
  // other properties
}

const Payment: React.FC<PaymentProps> = ({
  openPaymentdialog,
  handleClosePaymentDialog,
  setLoading,
  stopSetLoading,
  cid,
  disposableKitCount,
  circularLoader,
  setCircularLoader,
}) => {
  const [openCouponDialog, setOpenCouponDialog] = React.useState(false);
  const [orderSummary, setOrderSummary] = React.useState(new OrderSummary());
  const apiService: ApiService = React.useMemo(() => new ApiService(), []);
  const [address, setAddress] = React.useState<Address | undefined>(undefined);
  const [calendarResponse, setCalendarResponse] = React.useState<
    CalendarResponse | undefined
  >(undefined);
  const [paymentMode, setPaymentMode] = React.useState<string | number>(2);
  const [buildTempSlot, setBuildTempSlot] = React.useState<
    BuildTempSlot | undefined
  >(undefined);
  const [hub, setHub] = React.useState<Hub | null>(null);
  const [wallet, setWallet] = React.useState<WalletAmount | undefined>(
    undefined
  );
  const [walletRedeem, setWalletRedeem] = React.useState(false);
  const [couponCode, setCouponCode] = React.useState("");
  const [suggestion, setSuggestion] = React.useState("");
  const [suggestionAutoFocus, setSuggestionAutoFocus] = React.useState(false);
  const [couponAutoFocus, setCouponAutoFocus] = React.useState(false);
  const [showCoupon, setShowCoupon] = React.useState(false);
  const [openPlans, setOpenPlans] = React.useState(false);
  const [openToolTipInfo, setOpenToolTipInfo] = React.useState(false);
  const [reloadFlagAfterSubsciption, setReloadFlagAfterSubsciption] =
    React.useState(false);
  const router = useRouter();
  const cartService: AddToCartService = React.useMemo(
    () => new AddToCartService(),
    []
  );
  const currencyCode = useCurrencyCode();
  const token = getToken();
  const couponCodeRef = React.useRef(couponCode);
  const suggestionRef = React.useRef(suggestion);
  const paymentModes: PaymentModeResponse[] = [];

  paymentModes.push(
    new PaymentModeResponse(
      "cod",
      2,
      "Pay After Service (Cash/Online/Wallet)",
      undefined,
      "",
      true,
      undefined,
      false,
      undefined,
      undefined,
      undefined
    )
  );

  paymentModes.push(
    new PaymentModeResponse(
      "card",
      1,
      "Credit/Debit Card",
      undefined,
      "credit-card.png",
      false,
      undefined,
      true,
      undefined,
      undefined,
      undefined
    )
  );

  paymentModes.push(
    new PaymentModeResponse(
      "upi",
      3,
      "UPI",
      undefined,
      "upi-new.png",
      false,
      undefined,
      true,
      undefined,
      undefined,
      undefined
    )
  );

  paymentModes.push(
    new PaymentModeResponse(
      "netbanking",
      4,
      "Net Banking",
      undefined,
      "bank-building.png",
      false,
      undefined,
      true,
      undefined,
      undefined,
      undefined
    )
  );
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMode(event.target.value);
  };

  const handleAddOrder = async (mainCart: any, payload: any) => {
    const cart: Cart | undefined = mainCart?.cartItemRequestsById.find(
      (item: any) => item.cid === cid
    );
    const orderRequest: OrderRequest = new OrderRequest();
    orderRequest.calendarResponse = calendarResponse ?? new CalendarResponse();
    orderRequest.buildTempSlot = buildTempSlot ?? new BuildTempSlot();
    orderRequest.bookingDate = calendarResponse?.date ?? "";
    orderRequest.bookingSlot = buildTempSlot?.slot ?? "";
    orderRequest.hubId = hub?.id?.toString() ?? "";
    orderRequest.addressId = address?.id?.toString() ?? "";
    orderRequest.paymentModeCode = paymentMode ?? 2;
    orderRequest.couponCode = couponCodeRef.current;
    orderRequest.suggestion = suggestionRef.current;
    orderRequest.tempSpId = buildTempSlot?.spid ?? 0;
    orderRequest.razorpayValidatePayment = {
      orderId: payload?.orderId,
      paymentId: payload?.paymentId,
      signature: payload?.signature,
      razorpayOrder: {
        amount: payload?.razorpayOrder?.amount,
        currency: payload?.razorpayOrder?.currency,
        id: payload?.razorpayOrder?.id,
        status: payload?.razorpayOrder?.status,
      },
    };
    // orderRequest.exclusiveSpId = 0;
    // orderRequest.tempSpId = null;
    // orderRequest.cid = "49";
    // orderRequest.walletRedeem = false;
    // orderRequest.couponCode = null;
    // orderRequest.razorpayValidatePayment = null;
    // orderRequest.chatDateAndTime = null;

    orderRequest.orderServiceRequests = (cart?.cartItemRequests ?? []).map(
      (item) => {
        const orderServiceRequest = new OrderServiceRequest();
        orderServiceRequest.quantity = item.count;
        orderServiceRequest.sid = item.sid;
        return orderServiceRequest;
      }
    );
    try {
      const response = await apiService.post(
        apiService.createOrder,
        orderRequest,
        getToken()!
      );
      const responseObject = response as ResponseObject;
      if (responseObject?.status == "success") {
        handleClosePaymentDialog();
        // localStorage.removeItem("cart");
        mainCart.cartItemRequestsById = mainCart.cartItemRequestsById.filter(
          (item: Cart) => item.cid !== cid
        );
        localStorage.setItem("cart", btoa(JSON.stringify(mainCart)));
        localStorage.setItem("count", "0");
        localStorage.setItem("oid", responseObject?.object?.oid);
        localStorage.removeItem("slot");
        setLoading(false);
        router.push("/thankyou");
      } else {
        stopSetLoading();
        alert(`Message:" ${responseObject?.message}`);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error creating order:", error);
      alert("Failed to create order. Please try again.");
    }
  };

  const validatePayment = async (
    response: any,
    amount: number,
    mainCart: any
  ) => {
    try {
      const payload = {
        orderId: response.razorpay_order_id,
        paymentId: response.razorpay_payment_id,
        razorpayOrder: {
          amount: amount,
          currency: "INR",
          id: response.razorpay_order_id,
          status: "captured",
        },
        signature: response.razorpay_signature,
      };

      const validationResponse = await apiService.post<ResponseObject>(
        apiService.razorpayValidation,
        payload,
        token!
      );

      const result = await validationResponse;
      if (result?.status === "success") {
        await handleAddOrder(mainCart, payload);
      } else {
        alert("Payment verification failed.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      alert("Something went wrong while verifying the payment.");
      setLoading(false);
    }
  };

  const handlePayment = (
    e: any,
    orderId: string,
    amount: number,
    mainCart: any
  ) => {
    setLoading(false);
    const url =
      "https://play-lh.googleusercontent.com/Xfvj857cLrYU8GpQczqQYldDIPXYk2ow-bCuFVWQ633NmcKKx0iZ_hC5EQM-VbXC-y97"; // Optional

    const options = {
      key: "rzp_live_OwSnlsNKdctwe8", // Replace with your Key ID
      amount: amount,
      currency: "INR",
      name: "Salon Tym",
      description: "Subscription Amount",
      image: url,
      order_id: orderId,
      handler: async (response: any) => {
        setLoading(true);
        await validatePayment(response, amount, mainCart);
      },
      // prefill: {
      //   name: "Pankaj Kumar",
      //   email: "pankajsing555@gmail.com",
      //   contact: "8384064378",
      // },
      theme: {
        color: "#15741a",
      },
    };
    const paymentObject = new (window as any).Razorpay(options);

    paymentObject.on("payment.failed", (response: any) => {
      alert(`Payment Failed: ${response.error.description}`);
    });

    paymentObject.open();
    e.preventDefault();
  };

  const loadRazorpaySDK = (orderId: string, amount: number, mainCart: any) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = (e) => handlePayment(e, orderId, amount, mainCart);
    document.body.appendChild(script);
  };

  const handleCreateOrder = async (amount: any, mainCart: any) => {
    setLoading(true);
    try {
      const payload = {
        amount: amount * 100,
        currency: "INR",
        receipt: "string",
      };
      const responseObject = await apiService.post<ResponseObject>(
        apiService.razorpayCreateOrder,
        payload,
        token!
      );
      if (responseObject?.status === "success") {
        const { id: orderId } = await responseObject?.object;
        loadRazorpaySDK(orderId, payload?.amount, mainCart);
      } else {
        setLoading(false);
        alert("Failed to create order. Please try again.");
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      setLoading(false);
      alert("Something went wrong while verifying the payment.");
    }
  };

  const handleProceedButton = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const mainCart: MainCart = cartService.getCart();
    if (!mainCart) {
      alert("No cart found.");
      return;
    }
    if (+paymentMode !== 2) {
      await handleCreateOrder(orderSummary?.finalAmount, mainCart);
    } else {
      await handleAddOrder(mainCart, null);
    }
  };

  const initMethod =
    React.useCallback(async (): Promise<OrderSummary | null> => {
      // console.log("start logging");
      let summary: OrderSummary | null = null;
      const orderRequest: OrderRequest = new OrderRequest();
      const calendarResponse: CalendarResponse = JSON.parse(
        atob(localStorage.getItem("calendar") as string)
      );
      const serviceHub: ServiceHub = JSON.parse(
        atob(localStorage.getItem("serviceHub") as string)
      );
      const slot: BuildTempSlot = JSON.parse(
        atob(localStorage.getItem("slot") as string)
      );
      const address: Address = JSON.parse(
        atob(localStorage.getItem("address") as string)
      );
      setAddress(address);
      setHub(serviceHub);
      setCalendarResponse(calendarResponse);
      setBuildTempSlot(slot);
      const mainCart: MainCart = cartService.getCart();
      if (!mainCart) {
        alert("No cart found.");
        return null;
      }
      const cart: Cart | undefined = mainCart?.cartItemRequestsById.find(
        (item: any) => item.cid === cid
      );
      orderRequest.calendarResponse = calendarResponse;
      orderRequest.buildTempSlot = slot;
      orderRequest.tempSpId = slot.spid;
      orderRequest.bookingDate = calendarResponse.date;
      orderRequest.walletRedeem = walletRedeem;
      orderRequest.bookingSlot = slot.slot;
      orderRequest.hubId = serviceHub.id?.toString();
      orderRequest.addressId = address?.id?.toString() ?? "";
      orderRequest.couponCode = couponCodeRef.current;
      orderRequest.suggestion = suggestionRef.current;
      orderRequest.disposableKitCount = disposableKitCount;
      orderRequest.orderServiceRequests = (cart?.cartItemRequests ?? []).map(
        (item) => {
          const orderServiceRequest: OrderServiceRequest =
            new OrderServiceRequest();
          orderServiceRequest.quantity = item.count;
          orderServiceRequest.sid = item.sid;
          return orderServiceRequest;
        }
      );
      await apiService
        .post(apiService.orderSummery, orderRequest, getToken()!)
        .then((value: unknown) => {
          const orderSummaryResponse = value as ResponseObject;
          if (orderSummaryResponse?.status == "success") {
            const result = orderSummaryResponse.object as OrderSummary;
            setOrderSummary(result);
            summary = result;
          }
        });
      await apiService
        .get(apiService.customer, getToken()!)
        .then((value: unknown) => {
          const responseObject = value as ResponseObject;
          if (responseObject?.status == "success") {
            setWallet((responseObject.object as Customer).referralWallet);
            setReloadFlagAfterSubsciption(false);
          }
        });
      setCircularLoader(false);
      return summary;
    }, [
      apiService,
      cartService,
      cid,
      walletRedeem,
      reloadFlagAfterSubsciption,
      disposableKitCount,
    ]);

  React.useEffect(() => {
    initMethod();
  }, [initMethod]);

  React.useEffect(() => {
    couponCodeRef.current = couponCode;
    suggestionRef.current = suggestion;
  }, [couponCode, suggestion]);

  const handleChangeCheckBox = (e: any) => {
    if (wallet && wallet?.walletAmount > 0) {
      setCircularLoader(true);
      setWalletRedeem(e.target.checked);
      trackEvent("WALLET_REDEEM", null);
    } else {
      toast.error("Insufficient Balance !", {
        duration: 5000,
        style: {
          padding: "16px",
        },
      });
    }
  };

  const handleInputChange = (event: any) => {
    setSuggestionAutoFocus(false);
    setCouponAutoFocus(true);
    setCouponCode(event.target.value);
  };
  const handleSuggestions = (event: any) => {
    setCouponAutoFocus(false);
    setSuggestionAutoFocus(true);
    setSuggestion(event.target.value);
  };
  const handleApplyCoupon = async () => {
    if (couponCodeRef.current) {
      setCircularLoader(true);
      const summary = await initMethod();
      if (summary && summary?.couponDiscount <= 0) {
        setShowCoupon(true);
        setOpenCouponDialog(true);
        trackEvent("COUPON_APPLIED", null);
      }
    } else {
      setShowCoupon(false);
    }
  };
  const handleRemoveCoupon = () => {
    setCircularLoader(true);
    setCouponCode("");
    couponCodeRef.current = "";
    initMethod();
  };
  const handleCloseCouponDialog = () => {
    setOpenCouponDialog(false);
  };

  const Input = styled(TextField)(({ theme }) => ({
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      display: "none",
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    borderRadius: "50px",
  }));

  const PaymentsTypeStyle = paymentMethodRowSx;

  const HoverSpan = styled("span")({
    cursor: "pointer",
    "&:hover": {
      color: "black",
    },
  });

  const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: "#15741a",
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#15741a",
      color: "white",
    },
  }));

  return (
    <React.Fragment>
      <Dialog
        open={openPaymentdialog}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          justifyContent: "center",
          p: 0,
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(15, 23, 17, 0.45)",
          },
        }}
        PaperProps={{
          sx: paymentDialogPaperSx,
        }}
      >
        <DialogContent
          sx={{
            overflow: "auto",
            flex: "1 1 auto",
            minHeight: 0,
            px: { xs: 2, sm: 2.5 },
            pt: 0,
            pb: 2,
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 999,
              background: alpha(PAYMENT_MINT, 0.28),
            },
          }}
        >
          <Box
            sx={{
              background: paymentGradient,
              mx: { xs: -2, sm: -2.5 },
              mt: 0,
              px: { xs: 2, sm: 2.5 },
              py: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    fontWeight: 800,
                    color: "#fff",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.25,
                  }}
                >
                  Payment summary
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha("#fff", 0.88),
                    display: "block",
                    mt: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  Confirm amount, address, and payment method
                </Typography>
              </Box>
              <IconButton
                aria-label="Close payment"
                onClick={handleClosePaymentDialog}
                sx={{
                  color: "#fff",
                  bgcolor: alpha("#fff", 0.14),
                  "&:hover": { bgcolor: alpha("#fff", 0.24) },
                  flexShrink: 0,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 0rem 5px",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
              MRP Price
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {currencyCode}
              {orderSummary.totalMrp}
            </Typography>
          </Box>
          {orderSummary?.totalAmount && orderSummary.totalAmount > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "2px 0rem 5px",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
                Total Amount
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {currencyCode}
                {orderSummary?.totalAmount}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          {orderSummary?.walletRedeem && orderSummary.walletRedeem > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "2px 0rem 5px",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
                Wallet redeem
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                - {currencyCode}
                {orderSummary?.walletRedeem}
              </Typography>
            </Box>
          ) : (
            <></>
          )}

          {orderSummary?.primeTimeCharge && orderSummary.primeTimeCharge > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "2px 0rem 5px",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
                Prime time charges
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {currencyCode}
                {orderSummary?.primeTimeCharge}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          {orderSummary?.cancellationCharges &&
          orderSummary.cancellationCharges > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "2px 0rem 5px",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
                Cancellation Charges
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {currencyCode}
                {orderSummary?.cancellationCharges}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          {orderSummary?.couponDiscount && orderSummary.couponDiscount > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "2px 0rem 5px",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
                Coupon discount
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                - {currencyCode}
                {orderSummary?.couponDiscount}
              </Typography>
            </Box>
          ) : (
            <></>
          )}

          {orderSummary?.transportationCharge &&
          orderSummary.transportationCharge > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "2px 0rem 5px",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
                Transportation charges
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {currencyCode}
                {orderSummary?.transportationCharge}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          {orderSummary?.disposableKitAmount &&
          orderSummary.disposableKitAmount > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "2px 0rem 5px",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
                Safety & Hygiene Kit &nbsp;
                <BootstrapTooltip title="Information" placement="top">
                  <InfoOutlinedIcon
                    sx={{
                      color: "#000",
                      cursor: "pointer",
                      fontSize: "19px",
                    }}
                    onClick={() => setOpenToolTipInfo(true)}
                  />
                </BootstrapTooltip>
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {currencyCode}
                {orderSummary?.disposableKitAmount}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 0rem 5px",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "green" }}
            >
              Total discount
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "green" }}
            >
              - {currencyCode}
              {orderSummary.totalDiscount}
            </Typography>
          </Box>

          <Divider />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 0rem",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Amount Payable
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {circularLoader ? (
                <CircularLoader />
              ) : (
                <>
                  {currencyCode}
                  {orderSummary.finalAmount}
                </>
              )}
            </Typography>
          </Box>
          {/* ----------------------payment plans--------------- */}
          {!orderSummary?.customerSubscribed ? (
            <Box
              sx={{
                border: `1px solid ${alpha(PAYMENT_MINT, 0.22)}`,
                borderRadius: "14px",
                padding: "12px 14px",
                marginTop: "1rem",
                bgcolor: alpha(PAYMENT_MINT, 0.06),
                boxShadow: `0 4px 16px ${alpha(PAYMENT_MINT_DARK, 0.06)}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    color: PAYMENT_MINT_DARK,
                    fontWeight: 700,
                  }}
                  variant="subtitle2"
                >
                  Subscription discount
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: PAYMENT_MINT_DARK,
                    fontVariantNumeric: "tabular-nums",
                  }}
                  variant="subtitle2"
                >
                  − {currencyCode}
                  {orderSummary?.subscriptionDiscount}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Get extra {currencyCode}
                  {orderSummary?.subscriptionDiscount} off with a plan.
                </Typography>
                <Button
                  type="button"
                  size="small"
                  sx={{
                    ...paymentPrimaryButtonSx,
                    py: 0.5,
                    px: 1.75,
                    minHeight: 32,
                    fontSize: "0.8rem",
                  }}
                  onClick={() => setOpenPlans(true)}
                >
                  {!orderSummary?.customerSubscribed
                    ? "Apply now"
                    : "Upgrade"}
                </Button>
              </Box>
            </Box>
          ) : (
            ""
          )}
          {/* -------------------------payment method ----------------------- */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: PAYMENT_MINT_DARK,
              letterSpacing: "-0.02em",
              margin: "1.25rem 0 0.75rem",
            }}
          >
            Choose payment method
          </Typography>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue="female"
            name="radio-buttons-group"
            value={paymentMode}
            onChange={handleChange}
          >
            {paymentModes.map((item, i) => (
              <FormControlLabel
                key={i}
                sx={PaymentsTypeStyle}
                title={item?.name}
                value={item.code}
                control={<Radio size="small" />}
                componentsProps={{
                  typography: {
                    component: "div",
                    sx: {
                      width: "100%",
                      cursor: "pointer",
                    },
                  },
                }}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography sx={{ marginLeft: "0px" }} variant="caption">
                      {item.name}
                    </Typography>
                    {/* <Icon>ftf</Icon> */}
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          <Divider />
          {circularLoader ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width="100%"
                height={55}
                animation="wave"
                sx={{ borderRadius: "50px", margin: "1rem auto" }}
              />
            ))
          ) : (
            <>
              <Box
                sx={PaymentsTypeStyle}
                style={{ marginTop: "1rem", padding: "10px 1rem 10px 0px" }}
              >
                <Box sx={{ paddingLeft: "20px", lineHeight: "0px" }}>
                  <Typography variant="subtitle2" sx={{ color: "black" }}>
                    Wallet Balance
                  </Typography>
                  <Typography variant="caption">
                    {currencyCode}
                    {wallet
                      ? wallet.walletAmount > 0
                        ? wallet.walletAmount
                        : 0
                      : 0}{" "}
                    is available in your wallet
                  </Typography>
                </Box>
                <Checkbox
                  sx={{ color: PAYMENT_MINT, cursor: "pointer" }}
                  checked={walletRedeem}
                  onChange={handleChangeCheckBox}
                  color="success"
                />
              </Box>
              {wallet && wallet?.walletAmount <= 0 ? (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", padding: "0px 16px 8px" }}
                >
                  Insufficient Balance!
                </Typography>
              ) : (
                ""
              )}
              {walletRedeem ? (
                <Box
                  sx={{
                    margin: "-1rem 2rem 1rem",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "green" }}>
                    {orderSummary?.customerWalletResponse?.message}
                  </Typography>
                </Box>
              ) : (
                ""
              )}
              {orderSummary?.couponDiscount > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#fff",
                    borderRadius: "50px",
                    padding: "8px 16px",
                    margin: "2rem auto 1rem",
                    boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.1)",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "start",
                      alignItems: "center",
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        color: "#0acf0a",
                      }}
                    />
                    &nbsp; &nbsp;
                    <Typography>
                      The code has been successfully applied!
                    </Typography>
                    <Button
                      variant="text"
                      color="error"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: "bold",
                        color: "red",
                      }}
                      onClick={handleRemoveCoupon}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Input
                  margin="dense"
                  type="text"
                  label="Redeem Coupon"
                  value={couponCode}
                  autoFocus={couponAutoFocus}
                  onChange={handleInputChange}
                  fullWidth
                  InputProps={{
                    sx: {
                      paddingLeft: "5px",
                      borderRadius: "50px",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <HoverSpan onClick={handleApplyCoupon}>Apply</HoverSpan>
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    sx: {
                      typography: "caption",
                      paddingLeft: "10px",
                      "&.MuiInputLabel-shrink": {
                        paddingRight: "5px",
                        backgroundColor: "white",
                        color: PAYMENT_MINT,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: PAYMENT_MINT,
                      },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: PAYMENT_MINT,
                        borderRadius: "50px",
                      },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0, 0, 0, 0.2) ",
                    },
                    padding: "0px",
                    margin: "1rem auto",
                  }}
                />
              )}
              {showCoupon
                ? orderSummary.couponDiscount <= 0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        margin: "-5px 0px 12px 1rem",
                        color: "red",
                      }}
                    >
                      Coupon Not Valid
                    </Typography>
                  )
                : ""}
              <Input
                margin="dense"
                type="text"
                label="Any Suggestions?"
                autoFocus={suggestionAutoFocus}
                value={suggestion}
                onChange={handleSuggestions}
                fullWidth
                InputProps={{
                  sx: {
                    paddingLeft: "5px",
                    borderRadius: "50px",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <MessageIcon sx={{ color: PAYMENT_MINT }} />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: {
                    typography: "caption",
                    paddingLeft: "10px",
                    "&.MuiInputLabel-shrink": {
                      paddingRight: "5px",
                      backgroundColor: "white",
                      color: PAYMENT_MINT,
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: PAYMENT_MINT,
                      borderRadius: "50px",
                    },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.2) ",
                  },
                  padding: "0px",
                  margin: "0px 0px 1rem",
                }}
              />
            </>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "1rem 0px 0.5rem",
            }}
          >
            <LocationOnIcon sx={{ width: "18px" }} />
            <Typography
              sx={{
                paddingLeft: "1rem",
                padding: "0px 0x",
                fontWeight: "bold",
              }}
              variant="subtitle2"
            >
              Address
            </Typography>
          </Box>
          <Typography sx={{ paddingLeft: "2rem" }}>
            {address?.building +
              "," +
              address?.locality +
              "," +
              address?.formattedAddress}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "1rem 0px 0.5rem",
            }}
          >
            <CalendarMonthIcon sx={{ width: "18px" }} />
            <Typography
              sx={{
                paddingLeft: "1rem",
                padding: "0px 0x",
                fontWeight: "bold",
              }}
              variant="subtitle2"
            >
              Selected date and time
            </Typography>
          </Box>
          <Typography sx={{ paddingLeft: "2rem" }}>
            {calendarResponse?.date} | {buildTempSlot?.slot}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 0,
            justifyContent: "center",
            borderTop: `1px solid ${alpha(PAYMENT_MINT, 0.1)}`,
            bgcolor: "rgba(250, 252, 251, 0.9)",
          }}
        >
          <Box sx={{ width: "100%", textAlign: "center", maxWidth: 420, mx: "auto" }}>
            {circularLoader ? (
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "10px",
                  borderRadius: "999px",
                  width: "100%",
                  background: paymentGradient,
                  height: 48,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "auto",
                }}
              >
                <CircularLoader />
              </Box>
            ) : (
              <Button
                variant="contained"
                type="submit"
                sx={{
                  ...paymentPrimaryButtonSx,
                  width: "100%",
                }}
                onClick={handleProceedButton}
              >
                Proceed to payment
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <Coupon
        openCouponDialog={openCouponDialog}
        handleCloseCouponDialog={handleCloseCouponDialog}
      />
      <PlansPayments
        openPlans={openPlans}
        closePlans={() => {
          setOpenPlans(false);
        }}
        reloadFlagAfterSubsciption={() => setReloadFlagAfterSubsciption(true)}
      />
      <ToolTipSafetyInfo
        openInfo={openToolTipInfo}
        handleClose={() => setOpenToolTipInfo(false)}
      />
    </React.Fragment>
  );
};

export default Payment;
